import React, { useState, useEffect, useCallback } from 'react';
import { ReportData } from '../../types/reportTypes';
import { formatCurrency, formatPercentage } from '../../utils/formatters';
import {
  ReportHeaderContainer,
  ReportSection,
  ReportHeading,
  ReportSubheading,
  KPICardGrid,
  InsightsPanel,
} from './ReportResponsiveStyles';
import {
  SwipeableContainer,
  PinchZoomContainer,
  TouchFriendlyButton,
  CardSwipeList,
  useScreenSize,
  useTouchCapability
} from './MobileReportInteractions';

interface MobileOptimizedReportViewProps {
  data: ReportData;
  onExportPDF?: () => void;
}

/**
 * Mobile optimized view for marketing reports with enhanced touch interactions
 * Includes swipe navigation, touch-friendly UI elements, and responsive layouts 
 */
const MobileOptimizedReportView: React.FC<MobileOptimizedReportViewProps> = ({
  data,
  onExportPDF
}) => {
  // Get screen size info
  const screenSize = useScreenSize();
  const isTouchDevice = useTouchCapability();
  
  // State for the active section
  const [activeSection, setActiveSection] = useState<string>('kpi');
  
  // State for active charts
  const [activeChart, setActiveChart] = useState<'sources' | 'adspend' | 'trade'>('sources');
  
  // Format month name
  const monthName = data?.period?.month 
    ? new Date(2000, data.period.month - 1).toLocaleString('default', { month: 'long' })
    : "Report";
  
  // Handle swipe navigation between sections
  const handleSwipeLeft = () => {
    // Navigate to next section
    switch (activeSection) {
      case 'kpi':
        setActiveSection('charts');
        break;
      case 'charts':
        setActiveSection('sources');
        break;
      case 'sources':
        setActiveSection('jobs');
        break;
      default:
        break;
    }
  };
  
  const handleSwipeRight = () => {
    // Navigate to previous section
    switch (activeSection) {
      case 'jobs':
        setActiveSection('sources');
        break;
      case 'sources':
        setActiveSection('charts');
        break;
      case 'charts':
        setActiveSection('kpi');
        break;
      default:
        break;
    }
  };
  
  // Calculate combined metrics for lead source performance
  const sourcePerformance = React.useMemo(() => {
    if (!data?.salesBySource || !Array.isArray(data.salesBySource)) {
      return [];
    }
    
    return data.salesBySource.map(salesItem => {
      // Find matching ad spend
      const adSpendItem = (data.adSpendBySource || []).find(
        item => item.source.toLowerCase().includes(salesItem.source.toLowerCase()) || 
                salesItem.source.toLowerCase().includes(item.source.toLowerCase())
      );
      
      const adSpend = adSpendItem ? adSpendItem.spend : 0;
      
      return {
        source: salesItem.source,
        totalSales: salesItem.totalSales,
        count: salesItem.count,
        grossProfit: salesItem.grossProfit,
        adSpend: adSpend,
        roi: salesItem.grossProfit / (adSpend || 1), // Avoid division by zero
        costPerSale: adSpend / (salesItem.count || 1) // Avoid division by zero
      };
    });
  }, [data?.salesBySource, data?.adSpendBySource]);
  
  // Sort sources by ROI
  const sortedSourcePerformance = React.useMemo(() => {
    if (!sourcePerformance || !sourcePerformance.length) {
      return [];
    }
    return [...sourcePerformance].sort((a, b) => b.roi - a.roi);
  }, [sourcePerformance]);
  
  // Group job data by trade
  const jobsByTrade = React.useMemo(() => {
    if (!data?.jobData || !Array.isArray(data.jobData)) {
      return {
        "Roofing": [],
        "Windows & Doors": [],
        "Other": []
      };
    }
    
    return {
      "Roofing": (data.jobData || []).filter(job => job.trade === "Roofing"),
      "Windows & Doors": (data.jobData || []).filter(job => job.trade === "Windows & Doors"),
      "Other": (data.jobData || []).filter(job => job.trade !== "Roofing" && job.trade !== "Windows & Doors")
    };
  }, [data?.jobData]);
  
  // Color scheme for source performance
  const SOURCE_COLORS = {
    high: '#00C49F',    // Good ROI (green)
    medium: '#FFBB28',  // Medium ROI (yellow)
    low: '#FF8042',     // Low ROI (orange)
    negative: '#FF6666' // Negative ROI (red)
  };
  
  // Function to apply color based on ROI performance
  const getROIColor = (roi: number) => {
    if (roi <= 0) return SOURCE_COLORS.negative;
    if (roi < 1) return SOURCE_COLORS.low;
    if (roi < 3) return SOURCE_COLORS.medium;
    return SOURCE_COLORS.high;
  };
  
  // Render all available sections
  const renderKPISection = () => (
    <ReportSection>
      <ReportHeading>Key Performance Metrics</ReportHeading>
      <KPICardGrid>
        <div className="bg-blue-50 p-4 rounded-lg shadow-md">
          <div className="text-sm text-gray-600 mb-1">Total Sales</div>
          <div className="text-2xl font-bold text-blue-800">{formatCurrency(data.kpis?.totalSales || 0)}</div>
        </div>
        <div className="bg-green-50 p-4 rounded-lg shadow-md">
          <div className="text-sm text-gray-600 mb-1">Gross Profit</div>
          <div className="text-2xl font-bold text-green-800">{formatCurrency(data.kpis?.grossProfit || 0)}</div>
        </div>
        <div className="bg-orange-50 p-4 rounded-lg shadow-md">
          <div className="text-sm text-gray-600 mb-1">Ad Spend</div>
          <div className="text-2xl font-bold text-orange-800">{formatCurrency(data.kpis?.adSpend || 0)}</div>
        </div>
        <div className="bg-purple-50 p-4 rounded-lg shadow-md">
          <div className="text-sm text-gray-600 mb-1">Net Profit</div>
          <div className="text-2xl font-bold text-purple-800">{formatCurrency(data.kpis?.netProfit || 0)}</div>
        </div>
      </KPICardGrid>
      
      <div className="mt-4">
        <div className="text-lg font-semibold text-center">Net Profit Margin: <span className="text-blue-700">{formatPercentage(data.kpis?.netProfitPercent || 0)}</span></div>
        <div className="text-lg font-semibold text-center">Gross Profit Margin: <span className="text-green-700">{formatPercentage(data.kpis?.grossProfitPercent || 0)}</span></div>
      </div>
      
      <InsightsPanel className="mt-6">
        <p className="font-medium mb-1">March 2025 Performance Summary</p>
        <p className="text-sm">
          ROI Exteriors achieved <strong>{formatCurrency(data.kpis?.totalSales || 0)}</strong> in sales with 
          <strong> {formatCurrency(data.kpis?.grossProfit || 0)}</strong> gross profit. 
          The total ad spend was <strong>{formatCurrency(data.kpis?.adSpend || 0)}</strong>, 
          resulting in a net profit of <strong>{formatCurrency(data.kpis?.netProfit || 0)}</strong>.
        </p>
      </InsightsPanel>
      
      <div className="mt-6 text-sm text-center text-gray-500">
        <p>Swipe left to view visualizations →</p>
      </div>
    </ReportSection>
  );
  
  const renderChartOptions = () => (
    <div className="flex gap-2 mb-4 overflow-x-auto py-2 scrollbar-thin">
      <TouchFriendlyButton
        onClick={() => setActiveChart('sources')}
        className={`whitespace-nowrap rounded text-sm font-medium ${
          activeChart === 'sources' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-800'
        }`}
        ariaLabel="Show lead sources chart"
      >
        Lead Sources
      </TouchFriendlyButton>
      
      <TouchFriendlyButton
        onClick={() => setActiveChart('adspend')}
        className={`whitespace-nowrap rounded text-sm font-medium ${
          activeChart === 'adspend' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-800'
        }`}
        ariaLabel="Show ad spend chart"
      >
        Ad Spend
      </TouchFriendlyButton>
      
      <TouchFriendlyButton
        onClick={() => setActiveChart('trade')}
        className={`whitespace-nowrap rounded text-sm font-medium ${
          activeChart === 'trade' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-800'
        }`}
        ariaLabel="Show trade categories chart"
      >
        Trade Categories
      </TouchFriendlyButton>
    </div>
  );
  
  const renderChartSection = () => (
    <ReportSection>
      <ReportHeading>
        {activeChart === 'sources' ? 'Lead Source Performance' : 
         activeChart === 'adspend' ? 'Ad Spend Distribution' : 
         'Trade Categories'}
      </ReportHeading>
      
      {renderChartOptions()}
      
      <PinchZoomContainer className="rounded-lg bg-white shadow-md p-4 mb-4">
        {/* This would contain a chart component */}
        <div className="h-60 border rounded bg-gray-50 flex items-center justify-center">
          <p className="text-gray-500">
            {activeChart === 'sources' ? 'Lead Source Chart - Pinch to zoom' : 
             activeChart === 'adspend' ? 'Ad Spend Chart - Pinch to zoom' : 
             'Trade Categories Chart - Pinch to zoom'}
          </p>
        </div>
      </PinchZoomContainer>
      
      <div className="flex justify-between items-center text-sm text-gray-500 mt-2">
        <p>← Swipe right for KPIs</p>
        <p>Swipe left for details →</p>
      </div>
    </ReportSection>
  );
  
  const renderSourcesSection = () => (
    <ReportSection>
      <ReportHeading>Lead Source Performance</ReportHeading>
      
      <CardSwipeList
        items={sortedSourcePerformance}
        onSwipeLeft={(_, index) => console.log(`Swiped left on source ${index}`)}
        onSwipeRight={(_, index) => console.log(`Swiped right on source ${index}`)}
        renderItem={(source, index) => (
          <div className="p-4">
            <div className="flex justify-between items-start mb-2">
              <div className="font-bold text-gray-800">{source.source}</div>
              <div 
                className="text-sm px-2 py-1 rounded-full font-medium"
                style={{ backgroundColor: getROIColor(source.roi), color: 'white' }}
              >
                ROI: {source.roi.toFixed(1)}x
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-x-4 gap-y-2 mt-3 text-sm">
              <div>
                <div className="text-gray-600">Sales Count</div>
                <div className="font-medium">{source.count}</div>
              </div>
              <div>
                <div className="text-gray-600">Total Sales</div>
                <div className="font-medium">{formatCurrency(source.totalSales)}</div>
              </div>
              <div>
                <div className="text-gray-600">Ad Spend</div>
                <div className="font-medium">{formatCurrency(source.adSpend)}</div>
              </div>
              <div>
                <div className="text-gray-600">Gross Profit</div>
                <div className="font-medium">{formatCurrency(source.grossProfit)}</div>
              </div>
              <div>
                <div className="text-gray-600">Cost Per Sale</div>
                <div className="font-medium">{formatCurrency(source.costPerSale)}</div>
              </div>
            </div>
          </div>
        )}
      />
      
      <div className="flex justify-between items-center text-sm text-gray-500 mt-2">
        <p>← Swipe right for charts</p>
        <p>Swipe left for jobs →</p>
      </div>
    </ReportSection>
  );
  
  const renderJobsSection = () => (
    <ReportSection>
      <ReportHeading>Individual Jobs</ReportHeading>
      
      <div className="mb-4">
        <ReportSubheading>Roofing Jobs ({jobsByTrade["Roofing"].length})</ReportSubheading>
      </div>
      
      <CardSwipeList
        items={jobsByTrade["Roofing"]}
        renderItem={(job, index) => (
          <div className="p-4">
            <div className="font-bold text-gray-800 mb-1">{job.jobInfo}</div>
            <div className="text-sm text-gray-600 mb-3">Lead Source: {job.leadSource}</div>
            
            <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
              <div>
                <div className="text-gray-600">Contract Total</div>
                <div className="font-medium">{formatCurrency(job.contractTotal)}</div>
              </div>
              <div>
                <div className="text-gray-600">Gross Margin</div>
                <div className="font-medium">{formatPercentage(job.grossMarginPct)}</div>
              </div>
              <div>
                <div className="text-gray-600">Gross Profit</div>
                <div className="font-medium">{formatCurrency(job.grossProfit)}</div>
              </div>
              <div>
                <div className="text-gray-600">Commission</div>
                <div className="font-medium">{formatCurrency(job.salesCommission)}</div>
              </div>
              <div>
                <div className="text-gray-600">Net Profit</div>
                <div className="font-medium">{formatCurrency(job.netProfit)}</div>
              </div>
            </div>
          </div>
        )}
      />
      
      <div className="mt-6">
        <ReportSubheading>Windows & Doors Jobs ({jobsByTrade["Windows & Doors"].length})</ReportSubheading>
      </div>
      
      <CardSwipeList
        items={jobsByTrade["Windows & Doors"]}
        renderItem={(job, index) => (
          <div className="p-4">
            <div className="font-bold text-gray-800 mb-1">{job.jobInfo}</div>
            <div className="text-sm text-gray-600 mb-3">Lead Source: {job.leadSource}</div>
            
            <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
              <div>
                <div className="text-gray-600">Contract Total</div>
                <div className="font-medium">{formatCurrency(job.contractTotal)}</div>
              </div>
              <div>
                <div className="text-gray-600">Gross Margin</div>
                <div className="font-medium">{formatPercentage(job.grossMarginPct)}</div>
              </div>
              <div>
                <div className="text-gray-600">Gross Profit</div>
                <div className="font-medium">{formatCurrency(job.grossProfit)}</div>
              </div>
              <div>
                <div className="text-gray-600">Commission</div>
                <div className="font-medium">{formatCurrency(job.salesCommission)}</div>
              </div>
              <div>
                <div className="text-gray-600">Net Profit</div>
                <div className="font-medium">{formatCurrency(job.netProfit)}</div>
              </div>
            </div>
          </div>
        )}
      />
      
      <div className="flex justify-start items-center text-sm text-gray-500 mt-4">
        <p>← Swipe right for sources</p>
      </div>
    </ReportSection>
  );
  
  // Render navigation dots
  const renderNavigationDots = () => (
    <div className="flex justify-center gap-2 mt-6 mb-2">
      <button 
        onClick={() => setActiveSection('kpi')}
        className={`w-3 h-3 rounded-full ${activeSection === 'kpi' ? 'bg-blue-600' : 'bg-gray-300'}`}
        aria-label="Go to KPI section"
      />
      <button 
        onClick={() => setActiveSection('charts')}
        className={`w-3 h-3 rounded-full ${activeSection === 'charts' ? 'bg-blue-600' : 'bg-gray-300'}`}
        aria-label="Go to charts section"
      />
      <button 
        onClick={() => setActiveSection('sources')}
        className={`w-3 h-3 rounded-full ${activeSection === 'sources' ? 'bg-blue-600' : 'bg-gray-300'}`}
        aria-label="Go to sources section"
      />
      <button 
        onClick={() => setActiveSection('jobs')}
        className={`w-3 h-3 rounded-full ${activeSection === 'jobs' ? 'bg-blue-600' : 'bg-gray-300'}`}
        aria-label="Go to jobs section"
      />
    </div>
  );
  
  return (
    <div className="bg-white rounded-lg">
      {/* Header with Logo and Title */}
      <ReportHeaderContainer className="border-b pb-4 mb-6">
        <div className="w-full text-center">
          <img
            src="https://roiconstruction.com/wp-content/uploads/2024/04/Marylands-Premier-Roofer-ROI-Construction-Roofing-Siding-Windows-Doors.png"
            alt="ROI Exteriors Logo"
            className="h-16 mx-auto mb-2"
          />
          <h1 className="text-xl font-bold text-gray-800">Marketing Performance Report</h1>
          <h2 className="text-gray-600">{monthName} {data.period?.year}</h2>
        </div>
      </ReportHeaderContainer>
      
      {/* Report Actions */}
      <div className="flex justify-center gap-3 mb-6">
        {onExportPDF && (
          <TouchFriendlyButton
            onClick={onExportPDF}
            className="bg-blue-600 text-white font-semibold rounded flex items-center"
            ariaLabel="Export PDF"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M6 2a2 2 0 00-2 2v12a2 2 0 002 2h8a2 2 0 002-2V7.414A2 2 0 0015.414 6L12 2.586A2 2 0 0010.586 2H6zm5 6a1 1 0 10-2 0v3.586l-1.293-1.293a1 1 0 10-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L11 11.586V8z" clipRule="evenodd" />
            </svg>
            Export PDF
          </TouchFriendlyButton>
        )}
        
      </div>
      
      {/* Swipeable Content Container */}
      <SwipeableContainer
        onSwipeLeft={handleSwipeLeft}
        onSwipeRight={handleSwipeRight}
        className="overflow-hidden"
      >
        {/* Conditionally render the active section */}
        {activeSection === 'kpi' && renderKPISection()}
        {activeSection === 'charts' && renderChartSection()}
        {activeSection === 'sources' && renderSourcesSection()}
        {activeSection === 'jobs' && renderJobsSection()}
      </SwipeableContainer>
      
      {/* Navigation Dots */}
      {renderNavigationDots()}
      
      {/* Fixed Footer */}
      <div className="mt-8 pt-4 border-t text-xs text-center text-gray-500">
        <p>Generated on {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
        <p>ROI Exteriors Marketing Performance Report</p>
      </div>
    </div>
  );
};

export default MobileOptimizedReportView;